import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { macroCondition, isDevelopingApp } from '@embroider/macros';
import { task } from 'ember-concurrency';
import { or, eq, and } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import { variation } from 'ember-launch-darkly';
import { not } from 'ember-truth-helpers';
import { queue } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';
import {
  HdsCardContainer,
  HdsIcon,
  HdsBadge,
  HdsDropdown,
  HdsTable,
  HdsLinkInline,
  HdsAlert,
} from '@hashicorp/design-system-components/components';
import {
  OperationFailureForResource,
  Loading,
  Flex,
  Typography,
  FlexGrid,
  ClipboardCopyButton,
  ZeroState,
  CoreModalConfirm,
  DetailSection,
  DefinitionList,
  Box,
  DetailCard,
} from 'core/components';
import { tooltip, boxMargin, boxPadding } from 'core/modifiers';
import {
  classNames,
  optionForSourceState,
  resourceIsBusy,
  resourceTypeFor,
} from 'core/helpers';
import DeletePeeringModal from 'hcp/components/page/hvn-peerings/delete-peering-modal';
import peeringDestinationInfo from 'hcp/helpers/peering-destination-info';
import LocationLink from 'hcp/components/location-link';
import networksConnectionSourceType from 'networks-common/helpers/networks-connection-source-type';
import {
  isAwsProviderNetwork,
  connectionPriority,
  sortNetworkConnectionsByStatePrecedence,
} from 'networks-common/utils/networks';
import {
  PEERING_PENDING_ACCEPTANCE_STATUS,
  PEERING_ACTIVE_STATUS,
} from 'networks-common/utils/peerings';
import { COMPLETED_CONFIGURATION_STEPS } from 'networks-common/utils/peerings';

/**
 *
 * `PageHvnsDetailView` renders the HashiCorp Virtual Networks Detail page.
 *
 *
 * ```
 * <Page::Hvns::Detail::View
 *   @model={{@model}}
 *   @canCreateConnection={{@canCreateConnection}}
 *   @canCreatePeerings={{@canCreatePeerings}}
 *   @canCreateTgwAttachments={{@canCreateTgwAttachments}}
 * />
 * ```
 *
 * @class PageHvnsDetailView
 *
 */
export default class PageHvnsDetailView extends Component {
  /**
   *
   * `model` is an object that contains a network response and dependencies response as attributes.
   * @argument model;
   * @type {Boolean}
   *
   */
  /**
   *
   * `canCreateConnection` is a boolean derived in the parent container that determines the logged-in
   * user's ability to create a connection; will be true when either canCreatePeerings OR canCreateTgwAttachments
   * is true
   * @argument canCreateConnection
   * @type {Boolean}
   *
   */
  /**
   *
   * `canCreatePeerings` is a boolean derived in the parent container that determines the logged-in
   * user's ability to create peering connections
   * @argument canCreatePeerings
   * @type {Boolean}
   *
   */
  /**
   *
   * `canCreateTgwAttachments` is a boolean derived in the parent container that determines the logged-in
   * user's ability to create tgw attachments
   * @argument canCreateTgwAttachments
   * @type {Boolean}
   *
   */
  @service api;
  @service operation;
  @service router;

  connectionPriority = connectionPriority;

  @tracked peeringToDelete = null;
  @tracked transitGatewayAttachmentToDelete = null;
  @tracked showDeleteModal = false;

  get provider() {
    return this.args.model.network.location.region.provider;
  }

  get isAws() {
    return isAwsProviderNetwork(this.args.model.network);
  }

  get networkConnections() {
    let peeringsAndTgws = [
      ...this.args.model.peerings.map((peering) => {
        let metadata = null;
        if (this.args.model.metadata) {
          let {
            metadata: {
              metadata: { peering: peeringsMetadata },
            },
          } = this.args.model;
          metadata = peeringsMetadata[peering.id];
          let state = peering.state;
          if (state === PEERING_ACTIVE_STATUS && metadata) {
            peering.state =
              COMPLETED_CONFIGURATION_STEPS.filter(
                (step) => !metadata.completion.includes(step)
              ).length > 0
                ? PEERING_PENDING_ACCEPTANCE_STATUS
                : state;
          }
        }
        return { ...peering, type: 'hvn_peering', metadata };
      }),
      ...this.args.model.tgwAttachments.map((tgw) => {
        return { ...tgw, type: 'hvn_transit_gateway_attachment' };
      }),
    ];

    return sortNetworkConnectionsByStatePrecedence(
      peeringsAndTgws,
      this.connectionPriority
    );
  }

  deleteTransitGatewayAttachment = task(async () => {
    let hvnId = this.args.model.network.id;
    let { organizationId, projectId } = this.args.model.network.location;
    let { id } = this.transitGatewayAttachmentToDelete;
    let operation;

    try {
      let resp = await this.api.network.deleteTGWAttachment(
        organizationId,
        projectId,
        hvnId,
        id
      );
      operation = resp.operation;
    } catch (e) {
      if (macroCondition(isDevelopingApp())) {
        console.error(e);
      }
      // TODO - make this a flash message
      throw e;
    }
    this.showDeleteModal = false;
    this.operation.addOperation(operation);
    this.refreshRoute();
  });

  refreshRoute = () => {
    let route = getOwner(this).lookup(
      `route:${this.router.currentRoute.parent.name}`
    );
    return route.refresh();
  };

  <template>
    {{#if
      (or
        @isDeleting
        (resourceIsBusy @model.network type=(resourceTypeFor "network"))
      )
    }}
      <OperationFailureForResource
        @resource={{@model.network}}
        @resourceType={{resourceTypeFor "network"}}
      />
      <Loading data-test-loading as |L|>
        <L.Header>
          {{if
            (eq @model.network.state "DELETING")
            (t "components.page.hvns.detail.deleting.header")
          }}
          {{if
            (eq @model.network.state "CREATING")
            (t "components.page.hvns.detail.initializing.header")
          }}
        </L.Header>
        <L.Message>
          {{if
            (eq @model.network.state "DELETING")
            (t "components.page.hvns.detail.deleting.message")
          }}
          {{if
            (eq @model.network.state "CREATING")
            (t "components.page.hvns.detail.initializing.message")
          }}
        </L.Message>
      </Loading>
    {{else}}
      {{#if (and (variation "hcp-hvn-guided-flow") this.isAws)}}
        <HdsCardContainer
          data-networks-cards-connection-details
          class="networks-cards-connection-details"
          @level="base"
          @hasBorder={{true}}
          {{boxPadding "md 2xl lg lg"}}
        >
          <Flex @direction="column" @gap="md">
            <Flex @alignItems="center" @gap="xl">
              <Flex
                @direction="column"
                @gap="2xs"
                @grow={{1}}
                @justifyContent="start"
              >
                <Typography
                  @variant="body1"
                  class="hds-typography-body-100 hds-font-weight-regular hds-foreground-faint"
                >
                  {{t "components.page.hvns.detail.general.labels.network-id"}}
                </Typography>
                <Flex @alignItems="center" @direction="row" @gap="md">
                  <Flex @alignItems="center" @gap="2xs">
                    <HdsIcon @name="hashicorp-color" @isInline={{true}} />
                    <span
                      class="hds-typography-body-200 hds-font-weight-semibold hds-foreground-primary"
                      data-networks-cards-connection-details-name
                    >
                      {{@model.network.id}}
                    </span>
                  </Flex>
                  <Flex @alignItems="center" @direction="row">
                    {{#let
                      (optionForSourceState "hvn" @model.network.state)
                      as |option|
                    }}
                      <HdsBadge
                        @text={{t option.text}}
                        @color={{option.color}}
                        @icon={{option.icon}}
                        @isIconOnly={{option.isIconOnly}}
                        @size={{option.size}}
                        @type={{option.type}}
                      />
                    {{/let}}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
            <hr />
            <FlexGrid as |G|>
              <G.Item @xs="12" @sm="12" @md="3" @lg="3">
                <Flex
                  @direction="column"
                  @gap="2xs"
                  @grow={{1}}
                  @justifyContent="start"
                >
                  <Typography
                    @variant="body1"
                    class="hds-typography-body-100 hds-font-weight-regular hds-foreground-faint"
                  >
                    {{t
                      "components.page.hvns.detail.general.labels.cloud-provider"
                    }}
                  </Typography>
                  <Flex @gap="2xs">
                    <Flex
                      @justifyContent="center"
                      data-networks-cards-connection-details-data
                    >
                      {{#let
                        (optionForSourceState "region" this.provider)
                        as |option|
                      }}
                        <Flex @alignItems="center" @direction="row" @gap="2xs">
                          <HdsIcon
                            @name={{option.icon}}
                            @isInline={{true}}
                            data-networks-cards-connection-details-state
                          />
                          <span class="hds-typography-body-200">
                            {{t option.text}}
                          </span>
                        </Flex>
                      {{/let}}
                    </Flex>
                  </Flex>
                </Flex>
              </G.Item>
              <G.Item @xs="12" @sm="12" @md="3" @lg="3">
                <Flex
                  @direction="column"
                  @gap="2xs"
                  @grow={{1}}
                  @justifyContent="start"
                >
                  <Typography
                    @variant="body1"
                    class="hds-typography-body-100 hds-font-weight-regular hds-foreground-faint"
                  >
                    {{t "components.page.hvns.detail.general.labels.region"}}
                  </Typography>
                  <Flex @gap="2xs">
                    <Flex
                      @justifyContent="center"
                      data-networks-cards-connection-details-data
                    >
                      {{#let
                        (optionForSourceState "region" this.provider)
                        as |option|
                      }}
                        <Flex @alignItems="center" @direction="row" @gap="2xs">
                          <HdsIcon
                            @name={{option.icon}}
                            @isInline={{true}}
                            data-networks-cards-connection-details-state
                          />
                          <span class="hds-typography-body-200">
                            {{@model.network.location.region.region}}
                          </span>
                        </Flex>
                      {{/let}}
                    </Flex>
                  </Flex>
                </Flex>
              </G.Item>
              <G.Item @xs="12" @sm="12" @md="3" @lg="3">
                <Flex
                  @direction="column"
                  @gap="2xs"
                  @grow={{1}}
                  @justifyContent="start"
                >
                  <Typography
                    @variant="body1"
                    class="hds-typography-body-100 hds-font-weight-regular hds-foreground-faint"
                  >
                    {{t
                      "components.page.hvns.detail.general.labels.cidr-block"
                    }}
                  </Typography>
                  <Flex @gap="2xs">
                    <Flex
                      @justifyContent="center"
                      data-networks-cards-connection-details-data
                    >
                      {{#let
                        (optionForSourceState "region" this.provider)
                        as |option|
                      }}
                        <Flex @alignItems="center" @direction="row" @gap="2xs">
                          <HdsIcon
                            @name={{option.icon}}
                            @isInline={{true}}
                            data-networks-cards-connection-details-state
                          />
                          <span class="hds-typography-body-200">
                            {{@model.network.cidrBlock}}
                          </span>
                        </Flex>
                      {{/let}}
                    </Flex>
                    <Flex @justifyContent="center">
                      <ClipboardCopyButton
                        title={{t
                          "components.networks.cards.connection-details.copy-button-title"
                        }}
                        @clipboardText={{@model.network.cidrBlock}}
                        @variant="no-success-text"
                      />
                    </Flex>
                  </Flex>
                </Flex>
              </G.Item>
            </FlexGrid>
          </Flex>
        </HdsCardContainer>
        {{#if
          (and
            (eq @model.peerings.length 0) (eq @model.tgwAttachments.length 0)
          )
        }}
          <ZeroState as |ZS|>
            <ZS.Header>
              {{t "components.page.hvn-peerings.list.empty.header"}}
            </ZS.Header>
            <ZS.Message>
              {{t
                "components.page.hvn-peerings.list.empty.message"
                provider=this.provider
              }}
            </ZS.Message>
            {{#if @canCreateConnection}}
              <ZS.Action>
                <HdsDropdown @listPosition="bottom-right" as |dd|>
                  <dd.ToggleButton
                    @icon="plus"
                    @color="secondary"
                    @text={{t
                      "components.page.hvns.detail.create-dropdown.guided-flow.title"
                    }}
                    data-test-create-connection-button
                    data-test-create-connection-button-view
                  />
                  {{#if @canCreatePeerings}}
                    <dd.Interactive
                      @text={{t
                        "components.page.hvns.detail.create-dropdown.guided-flow.peering.title"
                      }}
                      @route="cloud.orgs.detail.projects.detail.hvns.detail.peerings.create"
                      @model={{@model.network.id}}
                      @icon="connection"
                      data-test-create-peering
                    >
                      {{t
                        "components.page.hvns.detail.create-dropdown.guided-flow.peering.title"
                      }}
                    </dd.Interactive>
                    <dd.Description
                      @text={{t
                        "components.page.hvns.detail.create-dropdown.guided-flow.peering.description"
                        provider=@model.network.location.region.provider
                      }}
                    />
                  {{/if}}
                  {{#if (eq @model.network.location.region.provider "aws")}}
                    {{#if (and @canCreatePeerings @canCreateTgwAttachments)}}
                      <dd.Separator />
                    {{/if}}
                    {{#if @canCreateTgwAttachments}}
                      <dd.Interactive
                        @text={{t
                          "components.page.hvns.detail.create-dropdown.guided-flow.tgw.title"
                        }}
                        @route="cloud.orgs.detail.projects.detail.hvns.detail.transit-gateway-attachments.create"
                        @model={{@model.network.id}}
                        @icon="connection-gateway"
                        data-test-create-tgw
                      />
                      <dd.Description
                        @text={{t
                          "components.page.hvns.detail.create-dropdown.guided-flow.tgw.description"
                        }}
                      />
                    {{/if}}
                  {{/if}}
                </HdsDropdown>
              </ZS.Action>
            {{/if}}
          </ZeroState>
        {{else}}
          <div {{boxPadding "xl 0 0 0"}}>
            <div {{boxPadding "0 0 xl"}}>
              <Typography
                @variant="body1"
                class={{classNames
                  "hds-typography-display-300"
                  "hds-font-weight-bold"
                  "hds-foreground-strong"
                }}
              >
                {{t "components.page.hvns.table.title"}}
              </Typography>
            </div>
            <HdsTable
              @caption={{t "components.page.hvns.table.title"}}
              @valign="middle"
              data-test-table
            >
              <:head as |H|>
                <H.Tr>
                  <H.Th>
                    {{t "components.page.hvns.table.headers.hcp-peering-name"}}
                  </H.Th>
                  <H.Th>
                    {{t "components.page.hvns.table.headers.type"}}
                  </H.Th>
                  <H.Th>
                    {{t "components.page.hvns.table.headers.status"}}
                  </H.Th>
                  <H.Th>
                    {{t "components.page.hvns.table.headers.destination-name"}}
                  </H.Th>
                  <H.Th>
                    {{t
                      "components.page.hvns.table.headers.destination-region"
                    }}
                  </H.Th>
                  <H.Th>
                    {{t
                      "components.page.hvns.table.headers.destination-vpc-tgw"
                    }}
                  </H.Th>
                  <H.Th />
                </H.Tr>
              </:head>
              <:body as |B|>
                {{#each this.networkConnections key="id" as |connection|}}
                  <B.Tr data-test-hvn-connections-list-item>
                    <B.Td>
                      <Flex @alignItems="center" @direction="row" @gap="2xs">
                        <HdsIcon
                          @name="hashicorp-color"
                          @isInline={{true}}
                          data-test-peering-locked
                        />
                        {{#if
                          (eq connection.type "hvn_transit_gateway_attachment")
                        }}
                          <HdsLinkInline
                            @color="secondary"
                            @models={{array connection.id}}
                            @route="cloud.orgs.detail.projects.detail.hvns.detail.transit-gateway-attachments.detail"
                            class="hds-font-weight-medium"
                          >
                            {{connection.id}}
                          </HdsLinkInline>
                        {{else if (eq connection.type "hvn_peering")}}
                          {{#if connection.showLink}}
                            <HdsLinkInline
                              @color="secondary"
                              @models={{array connection.id}}
                              @route="cloud.orgs.detail.projects.detail.hvns.detail.peerings.detail"
                              class="hds-font-weight-medium"
                            >
                              {{connection.id}}
                            </HdsLinkInline>
                          {{else}}
                            {{connection.id}}
                          {{/if}}
                          {{#if connection.isLocked}}
                            <HdsIcon
                              @name="lock-fill"
                              @isInline={{true}}
                              {{tooltip
                                (t
                                  "components.page.hvn-peerings.list.tooltip.locked-peering"
                                )
                              }}
                              data-test-peering-locked
                            />
                          {{/if}}
                        {{/if}}
                      </Flex>
                    </B.Td>
                    <B.Td>
                      {{if
                        (eq connection.type "hvn_transit_gateway_attachment")
                        (t "components.page.hvns.table.text.tgw")
                        (t "components.page.hvns.table.text.peering")
                      }}
                    </B.Td>
                    <B.Td>
                      {{#let
                        (optionForSourceState
                          (networksConnectionSourceType connection)
                          connection.state
                        )
                        as |option|
                      }}
                        <HdsBadge
                          @text={{t option.text}}
                          @color={{option.color}}
                          @icon={{option.icon}}
                          @isIconOnly={{option.isIconOnly}}
                          @size={{option.size}}
                          @type={{option.type}}
                        />
                      {{/let}}
                    </B.Td>
                    {{#if (eq connection.type "hvn_peering")}}
                      {{#let
                        (peeringDestinationInfo connection)
                        as |destination|
                      }}
                        {{#let
                          (optionForSourceState "region" this.provider)
                          as |option|
                        }}
                          <B.Td>
                            <Flex
                              @alignItems="center"
                              @direction="row"
                              @gap="2xs"
                            >
                              <HdsIcon
                                @name={{option.icon}}
                                @isInline={{true}}
                                data-test-aws-provider-logo
                              />
                              <span>
                                {{connection.providerPeeringId}}
                              </span>
                            </Flex>
                          </B.Td>
                          <B.Td>
                            <Flex
                              @alignItems="center"
                              @direction="row"
                              @gap="2xs"
                            >
                              <HdsIcon
                                @name={{option.icon}}
                                @isInline={{true}}
                                data-test-aws-provider-logo
                              />
                              <span>
                                {{destination.region}}
                              </span>
                            </Flex>
                          </B.Td>
                          <B.Td>
                            <Flex
                              @alignItems="center"
                              @direction="row"
                              @gap="2xs"
                            >
                              <HdsIcon
                                @name={{option.icon}}
                                @isInline={{true}}
                                data-test-aws-provider-logo
                              />
                              <span>
                                {{destination.vpc}}
                              </span>
                            </Flex>
                          </B.Td>
                          <B.Td>
                            {{#if
                              (not
                                (or
                                  connection.isLocked
                                  (resourceIsBusy
                                    connection
                                    type=(resourceTypeFor "network-peering")
                                  )
                                )
                              )
                            }}
                              <HdsDropdown as |dd|>
                                <dd.ToggleIcon
                                  @icon="more-horizontal"
                                  @text={{t "components.form.more"}}
                                  @hasChevron={{false}}
                                  data-test-more-menu-trigger
                                />
                                <dd.Interactive
                                  @color="critical"
                                  @icon="trash"
                                  @text={{t "components.form.delete"}}
                                  id="modal-dialog-open-button-{{connection.id}}"
                                  data-test-delete-button
                                  {{on
                                    "click"
                                    (queue
                                      (set this "peeringToDelete" connection)
                                      (set this "showDeleteModal" true)
                                    )
                                  }}
                                />
                              </HdsDropdown>
                            {{/if}}
                          </B.Td>
                        {{/let}}
                      {{/let}}
                    {{else}}
                      {{#let
                        (optionForSourceState "region" this.provider)
                        as |option|
                      }}
                        <B.Td>
                          <Flex
                            @alignItems="center"
                            @direction="row"
                            @gap="2xs"
                          >
                            <HdsIcon
                              @name={{option.icon}}
                              @isInline={{true}}
                              data-test-aws-provider-logo
                            />
                            <span>
                              {{connection.providerTgwAttachmentId}}
                            </span>
                          </Flex>
                        </B.Td>
                        <B.Td>
                          <Flex
                            @alignItems="center"
                            @direction="row"
                            @gap="2xs"
                          >
                            <HdsIcon
                              @name={{option.icon}}
                              @isInline={{true}}
                              data-test-aws-provider-logo
                            />
                            <span>
                              {{connection.location.region.region}}
                            </span>
                          </Flex>
                        </B.Td>
                        <B.Td>
                          <Flex
                            @alignItems="center"
                            @direction="row"
                            @gap="2xs"
                          >
                            <HdsIcon
                              @name={{option.icon}}
                              @isInline={{true}}
                              data-test-aws-provider-logo
                            />
                            <span>
                              {{connection.providerData.awsData.tgwId}}
                            </span>
                          </Flex>
                        </B.Td>
                        <B.Td>
                          {{#unless
                            (resourceIsBusy
                              connection type="hashicorp.network.tgw-attachment"
                            )
                          }}
                            <HdsDropdown as |dd|>
                              <dd.ToggleIcon
                                @icon="more-horizontal"
                                @text={{t "components.form.more"}}
                                @hasChevron={{false}}
                              />
                              <dd.Interactive
                                @text={{t "components.form.delete"}}
                                @color="critical"
                                @icon="trash"
                                id="modal-dialog-open-button-{{connection.id}}"
                                data-test-delete-button
                                {{on
                                  "click"
                                  (queue
                                    (set
                                      this
                                      "transitGatewayAttachmentToDelete"
                                      connection
                                    )
                                    (set this "showDeleteModal" true)
                                  )
                                }}
                              />
                            </HdsDropdown>
                          {{/unless}}
                        </B.Td>
                      {{/let}}
                    {{/if}}
                  </B.Tr>
                {{/each}}
              </:body>
            </HdsTable>
          </div>
          {{#if this.peeringToDelete}}
            <DeletePeeringModal
              @model={{@model}}
              @peering={{this.peeringToDelete}}
              @isOpen={{this.showDeleteModal}}
              @openStateChanged={{queue
                (set this "showDeleteModal")
                (set this "peeringToDelete")
              }}
            />
          {{else if this.transitGatewayAttachmentToDelete}}
            {{#if this.showDeleteModal}}
              <CoreModalConfirm
                @confirmButtonColor="primary"
                @confirmButtonText={{t "components.form.delete"}}
                @confirmText={{t "components.core.modal.confirm.delete"}}
                @headerColor="critical"
                @headerIcon="alert-triangle"
                @headerText={{t
                  "components.page.hvn-transit-gateway-attachments.list.delete-modal.title"
                }}
                @onCancel={{queue
                  (set this "showDeleteModal")
                  (set this "allowDelete")
                  (set this "transitGatewayAttachmentToDelete")
                }}
                @onSubmit={{this.deleteTransitGatewayAttachment}}
              >
                <:body>
                  <Typography
                    @variant="body1"
                    class="hds-typography-body-200 hds-font-weight-semibold hds-foreground-primary"
                    {{boxMargin "0 0 md 0"}}
                  >
                    {{t
                      "components.page.hvn-transit-gateway-attachments.list.delete-modal.warning"
                      tgwId=this.transitGatewayAttachmentToDelete.providerData.awsData.tgwId
                      htmlSafe=true
                    }}
                  </Typography>
                  <Typography
                    @variant="body1"
                    class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                    {{boxMargin "0 0 md 0"}}
                  >
                    {{t
                      "components.page.hvn-transit-gateway-attachments.list.delete-modal.recreation"
                    }}
                  </Typography>
                </:body>
              </CoreModalConfirm>
            {{/if}}
          {{/if}}
        {{/if}}
      {{else}}
        <div data-test-network-details>
          <OperationFailureForResource
            @resource={{@model.network}}
            @resourceType={{resourceTypeFor "network"}}
          />
          {{#if
            (and
              (eq @model.peerings.length 0) (eq @model.tgwAttachments.length 0)
            )
          }}
            <FlexGrid as |G|>
              <G.Item @xs="12" @sm="12" @md="12" @lg="12">
                <div {{boxPadding "0 0 lg 0"}}>
                  <HdsAlert
                    @type="compact"
                    @color="warning"
                    data-test-configuration-banner-warning
                    as |A|
                  >
                    <A.Description>
                      {{t
                        "components.page.hvns.detail.banner.warning.message"
                        htmlSafe=true
                        provider=this.provider
                        hvnDocumentation="https://cloud.hashicorp.com/docs/hcp/networking"
                      }}
                    </A.Description>
                  </HdsAlert>
                </div>
              </G.Item>
            </FlexGrid>
          {{/if}}
          <FlexGrid as |G|>
            <G.Item @xs="12" @sm="6">
              <DetailSection
                @title={{t "components.page.hvns.detail.general.title"}}
              >
                <DefinitionList {{boxPadding "md 0 xl 0"}} as |DL|>
                  <DL.Key>
                    {{t
                      "components.page.hvns.detail.general.labels.network-id"
                    }}
                  </DL.Key>
                  <DL.Value>
                    {{@model.network.id}}
                  </DL.Value>
                  <DL.Key>
                    {{t
                      "components.page.hvns.detail.general.labels.cloud-provider"
                    }}
                  </DL.Key>
                  <DL.Value>
                    {{#let
                      (optionForSourceState
                        "region" @model.network.location.region.provider
                      )
                      as |option|
                    }}
                      <Flex @alignItems="center" @direction="row" @gap="2xs">
                        <HdsIcon @name={{option.icon}} @isInline={{true}} />
                        <span>
                          {{t option.text}}
                        </span>
                      </Flex>
                    {{/let}}
                  </DL.Value>
                  <DL.Key>
                    {{t "components.page.hvns.detail.general.labels.region"}}
                  </DL.Key>
                  <DL.Value>
                    {{@model.network.location.region.region}}
                  </DL.Value>
                  <DL.Key>
                    {{t "components.page.hvns.detail.general.labels.status"}}
                  </DL.Key>
                  <DL.Value data-test-state>
                    {{#let
                      (optionForSourceState "hvn" @model.network.state)
                      as |option|
                    }}
                      <HdsBadge
                        @text={{t option.text}}
                        @color={{option.color}}
                        @icon={{option.icon}}
                        @isIconOnly={{option.isIconOnly}}
                        @size={{option.size}}
                        @type={{option.type}}
                      />
                    {{/let}}
                  </DL.Value>
                  <DL.Key>
                    {{t
                      "components.page.hvns.detail.general.labels.cidr-block"
                    }}
                  </DL.Key>
                  <DL.Value>
                    {{@model.network.cidrBlock}}
                  </DL.Value>
                </DefinitionList>
              </DetailSection>
            </G.Item>
            <G.Item @xs="12" @sm="6">
              <DetailSection
                @title={{t
                  "components.page.hvns.detail.associated-resources.title"
                }}
                as |DS|
              >
                <Box @padding="lg 0 0 0">
                  {{#each @model.dependencies as |link|}}
                    <DetailCard as |DC|>
                      <DC.Header>
                        <Typography @variant="h3">
                          <LocationLink @link={{link}} />
                        </Typography>
                      </DC.Header>
                    </DetailCard>
                  {{else}}
                    <DS.ZeroState>
                      {{t
                        "components.page.hvns.detail.associated-resources.empty.message"
                      }}
                    </DS.ZeroState>
                  {{/each}}
                </Box>
              </DetailSection>
            </G.Item>
          </FlexGrid>
        </div>
      {{/if}}
    {{/if}}
  </template>
}
